import { createApp, h } from "vue";
import { createInertiaApp } from "@inertiajs/vue3";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";

// --- Plugins ---
import { createHead } from "@vueuse/head";
import VueTippy from "vue-tippy";
import Toast from "vue-toastification";
import Vue3TouchEvents from "vue3-touch-events";
import { ZiggyVue } from "ziggy-js";
import pinia from "@/stores/index";
import i18n from "@/i18n";

// --- Utilities & Helpers ---
import twMerge from "@/tw-merge";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ko.js";
import { setLocale } from "yup";
import { ko as yupKoLocale } from "yup-locales";

// --- Application Specific ---
import "../css/app.css";
import "@/bootstrap";
import "@/copyright";
import "@/router";
import stickableDirective from "@/directives/stickable";

// --- Constants ---
const appName = window.document.getElementsByTagName("title")[0]?.innerText || "핀틀";
const isProduction = process.env.NODE_ENV === "production";

// --- Initializations ---
dayjs.locale("ko");
dayjs.extend(relativeTime);
setLocale(yupKoLocale);

// --- Helper Functions ---

/**
 * Resolves page components, supporting Laravel Modules.
 * @param {string} name - The page name (e.g., 'Auth/Login' or 'Blog::Posts/Index').
 * @param {Record<string, Function|object>} pages - Page component imports (result of import.meta.glob).
 * @param {string|null} module - The module name if specified (e.g., 'Blog').
 * @returns {Promise<object>} - The resolved page component.
 */
const moduleResolvePageComponent = (name, pages, module) => {
  let paths = Object.keys(pages);
  if (module) {
    paths = paths.filter((it) => it.includes(`/Modules/${module}/`));
  }

  const pagePath = `/Pages/${name.replace(/\./g, "/")}.vue`;
  const foundPath = paths.find((path) => path.endsWith(pagePath));

  if (!foundPath) {
    throw new Error(`Page not found: ${name}${module ? ` in module ${module}` : ""}`);
  }

  const component = pages[foundPath];
  return typeof component === "function" ? component() : component;
};

/**
 * Inertia page resolver. Handles standard pages and module pages (Module::PageName).
 * @param {string} name - The page name provided by Inertia.
 * @returns {Promise<object>} - The resolved page component.
 */
const componentPathResolver = (name) => {
  const [module, page] = name.split("::");

  return page
    ? moduleResolvePageComponent(page, import.meta.glob("../../Modules/*/resources/js/Pages/**/*.vue"), module)
    : resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob("./Pages/**/*.vue"));
};

/**
 * Configures and registers Vue plugins.
 * @param {object} app - The Vue app instance.
 */
const registerPlugins = (app) => {
  // Core plugins
  app.use(pinia);
  app.use(i18n);
  app.use(ZiggyVue);
  app.use(stickableDirective);

  // Head management
  const head = createHead({ title: appName });
  app.use(head);

  // UI Plugins
  const tippyOption = { defaultProps: { animation: "shift-away", arrow: true } };
  app.use(VueTippy, tippyOption);

  const toastOption = {
    position: "top-right",
    timeout: 8000,
    transition: "Vue-Toastification__fade",
    closeOnClick: false,
    newestOnTop: true,
    pauseOnFocusLoss: false,
    pauseOnHover: true,
    showCloseButtonOnHover: false,
    hideProgressBar: false,
    icon: true,
    closeButton: "button",
  };
  app.use(Toast, toastOption);

  app.use(Vue3TouchEvents, { disableClick: true });
};

/**
 * Sets up global properties and provides.
 * @param {object} app - The Vue app instance.
 */
const setupGlobals = (app) => {
  app.mixin({ methods: { route: window.route } }); // Make Ziggy route helper available in components

  app.provide("twMerge", twMerge);
  app.provide("dayjs", dayjs);
};

/**
 * Initializes Sentry for error tracking in production.
 * @param {object} app - The Vue app instance.
 */
const initializeSentry = (app) => {
  if (!isProduction) {
    console.log("Not in production environment. Skipping Sentry initialization.");
    return;
  }

  import("@sentry/vue")
    .then((Sentry) => {
      app.mixin(Sentry.createTracingMixins({ trackComponents: true }));

      Sentry.init({
        app: app,
        dsn: import.meta.env.VITE_SENTRY_FRONT_DSN,
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        tracesSampleRate: 0.1,
        tracePropagationTargets: ["localhost", "https://pin.tl", "https://www.pintl.so", "https://pintl.so"],
        replaysSessionSampleRate: 0.1, // Adjust as needed
        replaysOnErrorSampleRate: 1.0, // Capture all replays for errors
      });

      Sentry.attachErrorHandler(app, { logErrors: true });
      console.log("Sentry initialized.");
    })
    .catch((error) => {
      console.error("Sentry initialization failed:", error);
    });
};

// --- Inertia App Setup ---
createInertiaApp({
  title: (title) => (title ? `${title} — ${appName}` : appName),
  progress: false, // Disable built-in progress indicator
  resolve: componentPathResolver,
  setup({ el, App, props, plugin }) {
    // 1. Create Vue App Instance
    const app = createApp({ render: () => h(App, props) });

    // 2. Register Inertia Plugin FIRST
    app.use(plugin);

    // 3. Register Other Plugins
    registerPlugins(app);

    // 4. Setup Global Properties & Provides
    setupGlobals(app);

    // 5. Initialize Sentry (Async, Production Only)
    initializeSentry(app);

    // 6. Mount App
    app.mount(el);

    // Return app instance (though not strictly necessary after mount)
    return app;
  },
});
